import { Popover2, Popover2InteractionKind } from "@blueprintjs/popover2";

import React, { ReactNode } from "react";
import Icon, { IconName } from "../Icon/icon";
import { Position } from "../../common/position";
import { Menu } from ".";
import classNames from "classnames";

export interface MenuItemProps {
  text: string;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  href?: string;
  icon?: IconName; // 왼쪽
  label?: string; // 오른쪽
  labelElement?: React.ReactNode;
  labelClassName?: string;
  tagName?: keyof JSX.IntrinsicElements;
  tabIndex?: number;
  truncate?: boolean;
  popoverProps?: {};
  dropdown?: boolean;
  onClick?: () => void;
}

const MenuItem = ({
  text,
  children,
  disabled = false,
  icon,
  label,
  labelElement,
  labelClassName,
  tagName = "a",
  truncate = true,
  popoverProps,
  dropdown,
  onClick,
  className,
  tabIndex = 0,
}: MenuItemProps) => {
  const hasIcon = icon != null;
  const hasSubmenu = children != null;

  //
  const maybeRenderLabel = (labelElement?: React.ReactNode) => {
    if (label == null && labelElement == null) return null;
    return (
      <span className={`menu-label ${labelClassName && labelClassName}`}>
        {label}
        {labelElement}
      </span>
    );
  };

  //
  const maybeRenderPopover = (
    target: JSX.Element,
    children?: React.ReactNode
  ) => {
    if (!children) return target;
    return (
      <Popover2
        autoFocus={false}
        captureDismiss={false}
        disabled={disabled}
        enforceFocus={false}
        hoverCloseDelay={50}
        interactionKind={Popover2InteractionKind.HOVER}
        position={Position.RIGHT_TOP}
        usePortal={false}
        {...popoverProps}
        content={<Menu>{children}</Menu>}
        minimal={true}
        openOnTargetFocus={false}
      >
        {target}
      </Popover2>
    );
  };

  // 📌 target 생성
  const target = React.createElement(
    tagName,
    {
      onClick: onClick,
      tabIndex: tabIndex,
      ...(disabled ? DISABLED_PROPS : {}),
      className: classNames({
        "menu-item": true,
        [`${className}`]: className,
        "is-truncated": truncate,
        "is-disabled": disabled,
      }),
    },
    // children
    hasIcon ? (
      <span className="menu-icon">
        <Icon name={icon} tabIndex={-1} />
      </span>
    ) : undefined,
    <div className="menu-text">{text}</div>,
    maybeRenderLabel(labelElement),
    hasSubmenu ? (
      <Icon name="ArrowRight" className="submenu-arrow-icon" tabIndex={-1} />
    ) : undefined
  );

  return (
    <li className={classNames({ "dropdown-item": dropdown })}>
      {maybeRenderPopover(target, children)}
    </li>
  );
};

export default MenuItem;

const DISABLED_PROPS: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
  href: undefined,
  onClick: undefined,
  onMouseDown: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  tabIndex: -1,
};

